import SEO from '../components/SEO/SEO'
import { useState, useEffect } from 'react'
import Layout from '../layout/Layout'
import { Disclosure } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/outline'
import ContactForm from '../components/ContactForm/ContactForm'
import ButtonLink from '../components/ButtonLink/ButtonLink'

const AdmissionNotification = () => {
  const [formList, SetFormList] = useState(null)
  const [loading, SetLoading] = useState(true)
  const [error, SetError] = useState(null)

  const faqs = [
    {
      question: "What's the best thing about Switzerland?",
      answer:
        "I don't know, but the flag is a big plus. Lorem ipsum dolor sit amet consectetur adipisicing elit. <br /> Quas cupiditate laboriosam fugiat.",
    },
  ]

  function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
  }

  return (
    <>
      <SEO />
      <Layout>
        <div className="relative">
          <div className="absolute inset-x-0 bottom-0 bg-gray-100 h-1/2" />
          <div className="mx-auto">
            <div className="relative">
              <div className="absolute inset-0">
                <img
                  className="object-cover w-full h-full"
                  src="videocallAtDesk.png"
                  alt="Healthcare provider on a video call"
                />
              </div>
              <div className="relative max-w-screen-xl px-4 py-16 mx-auto md:py-16 lg:py-32">
                <h1 className="text-3xl tracking-tight md:text-5xl text-darkBlue">
                  Notifications and Admissions
                </h1>
                <p className="max-w-lg mt-6 text-xl text-grey sm:max-w-3xl">
                  Inpatient admission notification helps in getting your <br />{' '}
                  claims paid correctly and on time.
                </p>
              </div>
            </div>
          </div>
        </div>

        <section>
          <div className="max-w-screen-xl px-4 py-12 mx-auto sm:px-6 lg:px-8 text-grey">
            <p className="text-xl">
              Inpatient admission notification isn&#39;t a substitute for prior
              authorization. Some services require prior authorization in
              addition to notification of admission and some services don’t
              require notification at all. It all depends on the type of
              admission and facility. See Prior Authorization Resources for more
              information. If you have questions, call{' '}
              <a href="tel:1-855-579-3879" className="font-bold text-green">
                855-579-3879
              </a>{' '}
              and press 6 for utilization management.
            </p>
            <p className="pt-6 text-xl">
              For Plan members, call customer service at{' '}
              <a href="tel:1-855-579-3879" className="font-bold text-green">
                855-579-3879
              </a>{' '}
              and press option 1.
            </p>

            <ButtonLink uri="https://content.evryhealth.com/Files/UM_Inpatient_Notification_Form.pdf">
              Download Inpatient Notification Form
            </ButtonLink>

            <div className="grid h-full grid-cols-1 py-12 md:grid-cols-12 md:gap-8">
              <div className="col-span-1 md:col-span-7 mb-10 md:mb-0 font-[sans-serif]">
                <div className="px-4 py-12 mx-auto max-w-7xl sm:py-16 sm:px-6 lg:px-8">
                  <div className="max-w-3xl mx-auto divide-y-2 divide-grey-200 text-grey">
                    <dl className="mt-6 space-y-6 divide-y divide-gray-200">
                      <Disclosure as="div" className="pt-6">
                        {({ open }) => (
                          <>
                            <dt className="text-lg">
                              <Disclosure.Button className="flex items-start justify-between w-full text-left">
                                <span className="text-xl text-blue">
                                  Services Requiring Notification within 3 business days
                                </span>
                                <span className="flex items-center ml-6 h-7">
                                  <ChevronDownIcon
                                    className={classNames(
                                      open ? '-rotate-180' : 'rotate-0',
                                      'h-6 w-6 transform'
                                    )}
                                    aria-hidden="true"
                                  />
                                </span>
                              </Disclosure.Button>
                            </dt>
                            <Disclosure.Panel
                              as="dd"
                              className="pr-12 mt-2 text-base"
                            >
                              <p className="mt-8">
                                Evry Health requires inpatient notification for
                                all hospital stays. If the service has already
                                been prior authorized, the prior authorization
                                will serve as notification. You do not have to
                                submit a separate notification for those same
                                services.
                                <br />
                                <br />
                                All unplanned hospital stays require length of
                                stay review for medical necessity within 3 business
                                days of hospital admission. Out-of-network
                                facilities are required to request authorization
                                after emergency screening and stabilization of
                                services within 3 business days. For instructions on
                                submission see “How to Submit Notification of
                                Admission and Extension of Services Request”.
                              </p>
                              <p className="mt-8">
                                Members with health plan benefits which exclude
                                out-of-network coverage will not be responsible
                                for facility charges where the facility failed
                                to request timely authorization for inpatient
                                and length of stay review after emergency room
                                services. Notification is the responsibility of
                                the facility providing the service.
                              </p>
                              <p className="mt-8">
                                The following services require notification for
                                admission within 3 business days and notification of
                                discharge within 24 hours:
                              </p>
                              <ul className="mt-4 ml-8 list-disc">
                                <li className="pb-2">
                                  Unplanned Admissions: ALL medical, maternity,
                                  and surgical inpatient admissions
                                </li>
                                <li className="pb-2">
                                  Observation Stays resulting from ER visit over
                                  48 hours
                                </li>
                                <li className="pb-2">
                                  Observation Stays, unanticipated after surgery
                                  or other procedure over 48 hours
                                </li>
                                <li className="pb-2">
                                  Maternity admission for vaginal delivery or
                                  C-section
                                </li>
                                <li className="pb-2">
                                  All Out-of-Network/Out of Area Stays or
                                  Admission
                                </li>
                              </ul>
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                      <Disclosure as="div" className="pt-6">
                        {({ open }) => (
                          <>
                            <dt className="text-lg">
                              <Disclosure.Button className="flex items-start justify-between w-full text-left">
                                <span className="text-xl text-blue">
                                  How to Submit Notification of Admission and
                                  Extension of Services Request
                                </span>
                                <span className="flex items-center ml-6 h-7">
                                  <ChevronDownIcon
                                    className={classNames(
                                      open ? '-rotate-180' : 'rotate-0',
                                      'h-6 w-6 transform'
                                    )}
                                    aria-hidden="true"
                                  />
                                </span>
                              </Disclosure.Button>
                            </dt>
                            <Disclosure.Panel
                              as="dd"
                              className="pr-12 mt-2 text-base"
                            >
                              <p className="mt-8">
                                Step 1: A notification must be submitted within
                                3 business days of admission. All notifications MUST be
                                faxed to 325-603-0541 to be reviewed. <br />
                                <br />
                                Step 2: If the facility deems an inpatient
                                admission is NOT required following emergency
                                room services, the facility would fax a
                                notification of discharge within 24 hours of
                                discharge.
                                <br />
                                <br />
                                Step 3: If the facility deems an inpatient
                                admission is required following emergency room
                                services and stabilization, the facility MUST
                                request authorization for extension of services
                                for medical necessity review and length of stay
                                request. Complete medical records are required
                                to begin the medical necessity review for length
                                of stay request.
                                <p className="mt-8">
                                  Medical records may include:
                                </p>
                                <ul className="mt-4 ml-8 list-disc">
                                  <li className="pb-2">
                                    Hospital demographic sheet
                                  </li>
                                  <li className="pb-2">
                                    Admitting history and physical, ER notes,
                                    clinical progress notes
                                  </li>
                                  <li className="pb-2">
                                    Medication list, vital signs, imaging, or
                                    other diagnostic procedure results. If the
                                    patient is transferred from another acute
                                    care hospital or is under observation in
                                    your hospital, please include:
                                    <ul className="mt-4 ml-8 list-disc">
                                      <li className="pb-2">
                                        Specialty consult note(s)
                                      </li>
                                      <li className="pb-2">
                                        Last 3 days of attending physician and
                                        nursing notes
                                      </li>
                                      <li className="pb-2">
                                        Estimated discharge date, discharge
                                        plan, and any foreseen barriers to
                                        discharge
                                      </li>
                                    </ul>
                                  </li>
                                </ul>
                                Step 4: If supplemental information is required,
                                Evry Health will be in contact with the facility
                                on what is required to conduct the review.
                                Please note, failure to submit the appropriate
                                documentation and lack of timely delivery may
                                result in a denial of coverage.
                                <br /> <br />
                                Note: Evry Health requires inpatient
                                notification of admission and length of stay
                                review for medical necessity within 3 business days of
                                hospital admission. Emergency services and
                                stabilization are not subject to authorization.
                                Out-of-Network facilities are required to
                                request authorization for further care or
                                treatment needed after stabilization of an
                                emergency medical condition within 3 business days.
                                Notification of admission and length of stay
                                review for medical necessity is the
                                responsibility of the facility providing the
                                service. If facility fails to meet time
                                requirements, the member cannot be billed for
                                these services.
                              </p>
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                      <Disclosure as="div" className="pt-6">
                        {({ open }) => (
                          <>
                            <dt className="text-lg">
                              <Disclosure.Button className="flex items-start justify-between w-full text-left">
                                <span className="text-xl text-blue">
                                  Medical Records
                                </span>
                                <span className="flex items-center ml-6 h-7">
                                  <ChevronDownIcon
                                    className={classNames(
                                      open ? '-rotate-180' : 'rotate-0',
                                      'h-6 w-6 transform'
                                    )}
                                    aria-hidden="true"
                                  />
                                </span>
                              </Disclosure.Button>
                            </dt>
                            <Disclosure.Panel
                              as="dd"
                              className="pr-12 mt-2 text-base"
                            >
                              <p className="mt-8">
                                Medical records need to be submitted to us prior
                                to discharge and discharge notification is
                                required. Please fax medical records to{' '}
                                <a
                                  href="tel:325-603-0541"
                                  className="font-bold text-green"
                                >
                                  325-603-0541
                                </a>
                                .
                              </p>
                              <p className="mt-6">
                                Medical records may include:
                              </p>
                              <ul className="mt-4 ml-8 list-disc">
                                <li className="mt-4">
                                  Hospital demographic sheet
                                </li>
                                <li className="mt-4">
                                  Admitting history and physical, transfer
                                  notes, ER notes
                                </li>
                                <li className="mt-4">
                                  Medication list, vital signs, labs, imaging,
                                  or other diagnostic procedure results
                                </li>
                              </ul>
                              <p className="mt-4">
                                If the patient is transferred from another acute
                                care hospital or is under observation in your
                                hospital, please include:
                              </p>
                              <ul className="mt-4 ml-8 list-disc">
                                <li className="mt-4">
                                  Specialty consult notes
                                </li>
                                <li className="mt-4">
                                  Last 3 days of attending physician and nursing
                                  notes
                                </li>
                                <li className="mt-4 mb-12">
                                  Estimated discharge date, discharge plan, and
                                  any foreseen barriers to discharge
                                </li>
                              </ul>
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                      <Disclosure as="div" className="pt-6">
                        {({ open }) => (
                          <>
                            <dt className="text-lg">
                              <Disclosure.Button className="flex items-start justify-between w-full text-left">
                                <span className="text-xl text-blue">
                                  How to Submit a Concurrent Review
                                </span>
                                <span className="flex items-center ml-6 h-7">
                                  <ChevronDownIcon
                                    className={classNames(
                                      open ? '-rotate-180' : 'rotate-0',
                                      'h-6 w-6 transform'
                                    )}
                                    aria-hidden="true"
                                  />
                                </span>
                              </Disclosure.Button>
                            </dt>
                            <Disclosure.Panel
                              as="dd"
                              className="pr-12 mt-2 text-base"
                            >
                              <p className="mt-8">
                                Concurrent review of services must be submitted
                                to Evry Health via fax at 325-603-0541 in a
                                timely manner in order for length of stay review
                                to be conducted.
                                <br />
                                <br />
                                When submitting length of stay request, please
                                include the following information:
                                <ul className="mt-4 ml-8 list-disc">
                                  <li className="mt-4">
                                    All physician, nursing, and specialty
                                    provider notes since the previous
                                    authorization
                                  </li>
                                  <li className="mt-4">
                                    Vital signs, physical assessments
                                  </li>
                                  <li className="mt-4">
                                    Medication list, labs, imaging, or other
                                    diagnostic testing that was performed
                                  </li>
                                  <li className="mt-4">
                                    Estimated discharge date, discharge plan,
                                    and any foreseen barriers to discharge
                                  </li>
                                  <li className="mt-4">
                                    Length of stay request/ next anticipated
                                    review date
                                  </li>
                                </ul>
                                <br />
                                Please note, failure to submit the appropriate
                                documentation and lack of timely delivery may
                                result in a denial of coverage. If facility
                                fails to meet time requirements, the member
                                cannot be billed for these services.
                              </p>
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                    </dl>
                  </div>
                </div>
              </div>
              <div className="col-span-1 md:col-span-5">
                <div className="flex justify-center h-full">
                  <div className="text-base text-grey md:mt-12">
                    <p>
                      <strong>Disclaimer:</strong> All attempts are made to
                      provide the most current information. However, this does
                      NOT guarantee payment. Payment of claims is dependent on
                      eligibility, covered benefits, provider contracts, correct
                      coding, and billing practices. For specific details,
                      please refer to the provider manual. If you are uncertain
                      that prior authorization is needed, please submit request
                      for an accurate response.
                    </p>
                    <p className="py-6">
                      It is important to verify Benefits and Eligibility with
                      Evry Health Plans for all services. The Services{' '}
                      <a
                        href="/prior-authorization"
                        className="cursor-pointer text-green hover:underline"
                      >
                        listed
                      </a>{' '}
                      may be governed by Evry Health Plans Medical Policies,
                      which may impact coverage decisions. All planned
                      admissions and any procedure listed on this website
                      require prior authorization, all unplanned admissions
                      require notification unless otherwise specified.
                    </p>
                    <p className="py-6">
                      Prior authorization is required before the service is
                      provided in non-emergent situations. Retroactive requests
                      will be denied unless there are extenuating circumstances.
                      All prior authorizations should be requested using Evry
                      Health Plans request form. Supporting documentation (e.g.,
                      notes and lab or radiology findings) should be sent with
                      all prior authorization requests.
                    </p>
                    <p className="py-6">
                      All Out-of-Network facilities are required to request
                      authorization for further care or treatment needed after
                      the stabilization of an emergency medical condition.
                      Members with health plan benefits which exclude out of
                      network coverage will not be responsible for facility
                      charges where the facility failed to request timely
                      authorization for inpatient and length of stay review. To
                      join our network contact Customer Service{' '}
                      <a
                        href="tel:1-855-579-3879"
                        className="inline-block font-bold text-green"
                      >
                        855-579-3879
                      </a>{' '}
                      for provider relations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    </>
  )
}

export default AdmissionNotification
